import { Component } from 'react'
import * as styles from 'components/features/pfm/VerticalBarChart/VerticalBarChart.css'
import * as cn from 'classnames'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { CommonChartValues } from 'interfaces/chart'

export type VerticalBarChartProps = {
  values: CommonChartValues
  options: {
    isCurrency?: boolean
  }
  othersName: string
  withComparison: boolean
}

type VerticalBarState = {
  height: number
  top: number
  value: number
}

type VerticalBarChartState = {
  memberBar: VerticalBarState
  peerBar: VerticalBarState
  otherBar: VerticalBarState
  axisTop: number
}

const MINIMUM_BAR_HEIGHT = .5

const buildState = (props: VerticalBarChartProps): VerticalBarChartState => {
  const { memberValue, peerValue, otherValue } = props.values
  const max = Math.max(memberValue, peerValue, otherValue)
  const min = Math.min(memberValue, peerValue, otherValue)

  const minMaxSameSign = Math.sign(max) === Math.sign(min)
  const overall = minMaxSameSign ?
    max > 0 ? max : -min :
    max + (-min)

  const axisTop = max > 0 ? max : 0
  const [memberBar, peerBar, otherBar] = [memberValue, peerValue, otherValue].map((value) => {
    const top = (value > 0 ? axisTop - value : axisTop) / overall * 100
    const percentage = Math.abs(value / overall * 100)
    const height = Math.max(percentage, MINIMUM_BAR_HEIGHT)

    return { top, height, value }
  })

  return {
    memberBar,
    peerBar,
    otherBar,
    axisTop: axisTop / overall * 100,
  }
}

class VerticalBarChart extends Component<VerticalBarChartProps, VerticalBarChartState> {
  static getDerivedStateFromProps (nextProps: VerticalBarChartProps) {
    return buildState(nextProps)
  }

  state = buildState(this.props)

  render () {
    const { memberBar, peerBar, otherBar, axisTop } = this.state
    const { withComparison } = this.props
    const allZero = memberBar.value === 0 && peerBar.value === 0 && otherBar.value === 0
    return (
      <figure className={ styles.verticalBarChart }>
        <div className={ cn(styles.verticalBarChartInner, { [styles.allZero]: allZero }) }>
          { this.renderBar(memberBar, styles.member, 'You') }
          { withComparison && this.renderBar(peerBar, styles.peers, 'Your Peers') }
          { withComparison && this.renderBar(otherBar, styles.others, this.props.othersName) }
          <div className={ styles.axis } style={{ top: `calc(${ axisTop }% - 1px)` }} />
        </div>
      </figure>
    )
  }

  renderBar ({ height, top, value }: VerticalBarState, barClassName: string, label: string) {
    const displayValue = this.props.options.isCurrency ? formatUSCurrency(value) : value
    const barStyles = { height: `${ height }%`, top: `${ top }%` }
    const barClasses = cn(styles.bar, barClassName, {
      [styles.negative]: value < 0,
      [styles.zeroValue]: height <= MINIMUM_BAR_HEIGHT,
    })
    return (
      <div style={ barStyles } className={ barClasses }>
        <span className={ cn(styles.barValue, barClassName) }>{ displayValue }</span>
        <span className={ cn(styles.barLabel, barClassName) }>{ label }</span>
      </div>
    )
  }
}

export default VerticalBarChart
